import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Car, FileWarning, Gavel, RotateCcw, ShieldAlert, XCircle } from 'lucide-react';
import { Button } from '../../Button';
import { MobileHeader } from '../MobileHeader';
import type { MobileNav } from '../../../types/mobile';
const reasons = [{
  label: 'কিউআর কোড পাওয়া যায়নি',
  hit: false
}, {
  label: 'গাড়ির তথ্য মিলেনি',
  hit: false
}, {
  label: 'চালক অনুমোদিত নয়',
  hit: false
}, {
  label: 'ফিটনেসের মেয়াদ শেষ',
  hit: true,
  note: 'মেয়াদ শেষ: ১০ মার্চ ২০২৬'
}, {
  label: 'লাইসেন্সের মেয়াদ শেষ',
  hit: true,
  note: 'BRTA-1122334 • ০৯ ফেব্রু ২০২৬'
}, {
  label: 'স্টিকার বাতিল করা হয়েছে',
  hit: false
}];
const actions = [{
  label: 'ই-ট্রাফিক প্রসিকিউশন মামলা দায়ের',
  icon: Gavel
}, {
  label: 'গাড়ি সাময়িক আটক / ডাম্পিং',
  icon: Car
}, {
  label: 'কাগজপত্র জব্দ ও সতর্কতা নোটিশ',
  icon: FileWarning
}];
export function InvalidScreen({
  nav
}: {
  nav: MobileNav;
}) {
  const [sheet, setSheet] = useState(false);
  const report = () => {
    nav.setReportContext('invalid');
    nav.go('report');
  };
  return <div className="relative flex h-full flex-col bg-slate-50" data-id="element-947" data-mp="3qq457xk2tswb">
      <MobileHeader title="যাচাইয়ের ফলাফল" subtitle="QR ID: CNG-000004" onBack={() => nav.reset('home')} data-id="element-948" data-mp="3crxo3t3ck7jc" />
      <div className="no-scrollbar flex-1 overflow-y-auto p-4" data-id="element-949" data-mp="3gspyipnygih7">
        <motion.div initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="rounded-xl border-2 border-red-600 bg-red-50 p-4 text-center" role="alert" data-id="element-950" data-mp="3gv3t6xeq3so8">
          <XCircle className="mx-auto h-14 w-14 text-red-600" strokeWidth={2.2} data-id="element-951" data-mp="37tvxmn3ld5xu" />
          <p className="mt-1 font-heading text-2xl font-bold text-red-700" data-id="element-952" data-mp="2t4zo0llvzkv4">✕ যাচাই ব্যর্থ</p>
          <p className="mt-1 text-xs text-red-800" data-id="element-953" data-mp="2y3jemzbfdktt">এই গাড়ির ক্ষেত্রে এক বা একাধিক অনিয়ম শনাক্ত হয়েছে।</p>
        </motion.div>

        <div className="mt-3 rounded-xl border border-border bg-white p-3.5" data-id="element-954" data-mp="2k5393bpau0d9">
          <div className="flex items-center justify-between" data-id="element-955" data-mp="2p1jvo1guo6o8">
            <div data-id="element-956" data-mp="umehhzyaiyd0">
              <p className="text-[11px] text-slate-500" data-id="element-957" data-mp="umwjld7mw6su">রেজিস্ট্রেশন নং</p>
              <p className="font-bold text-navy-900" data-id="element-958" data-mp="pumntgju8cin">ঢাকা-থ-১৩-৭৮৯০</p>
            </div>
            <div className="text-right" data-id="element-959" data-mp="kvs6yk5fky16">
              <p className="text-[11px] text-slate-500" data-id="element-960" data-mp="kwuwann3usez">মালিক</p>
              <p className="text-sm font-semibold text-navy-900" data-id="element-961" data-mp="guq1vmou1rra">মোসাঃ শাহনাজ বেগম</p>
            </div>
          </div>
        </div>

        <h2 className="mb-2 mt-4 text-xs font-semibold text-slate-500" data-id="element-962" data-mp="1djfo1p7ht6j2">সম্ভাব্য কারণসমূহ</h2>
        <ul className="overflow-hidden rounded-xl border border-border bg-white" data-id="element-963" data-mp="18mp9557fcgz8">
          {reasons.map(r => <li key={r.label} className={`flex items-center gap-3 border-b border-slate-100 px-3.5 py-2.5 last:border-0 ${r.hit ? 'bg-red-50/60' : ''}`} data-id="element-964" data-mp="14ihih2heoqyd">
              {r.hit ? <XCircle className="h-5 w-5 shrink-0 text-red-600" data-id="element-965" data-mp="3pat43cx6eo88" /> : <span className="h-5 w-5 shrink-0 rounded-full border-2 border-slate-200" data-id="element-966" data-mp="3p86vrb2go4oy" />}
              <div className="flex-1" data-id="element-967" data-mp="3k9vl4y4cdq5o">
                <p className={`text-[13px] ${r.hit ? 'font-semibold text-red-700' : 'text-slate-400'}`} data-id="element-968" data-mp="1zte7uilmygax">{r.label}</p>
                {r.note && <p className="text-[11px] text-red-600" data-id="element-969" data-mp="24taekplpwlu6">{r.note}</p>}
              </div>
              <span className={`text-[10px] font-semibold ${r.hit ? 'text-red-600' : 'text-slate-300'}`} data-id="element-970" data-mp="28tki7vm1f05v">{r.hit ? 'শনাক্ত' : 'প্রযোজ্য নয়'}</span>
            </li>)}
        </ul>
      </div>
      <div className="grid gap-2 border-t border-border bg-white p-3" data-id="element-971" data-mp="2ikf5n3b0s5sw">
        <Button onClick={report} className="h-11 w-full bg-red-600 hover:bg-red-700" data-id="element-972" data-mp="2niqkjsg696hz">
          <AlertTriangle className="h-4 w-4" data-id="element-973" data-mp="2nkjqt6mvhopg" /> অনিয়ম রিপোর্ট করুন
        </Button>
        <div className="grid grid-cols-2 gap-2" data-id="element-974" data-mp="2sfp9zxow4p9u">
          <Button variant="outline" onClick={() => setSheet(true)} className="h-10" data-id="element-975" data-mp="2wjoo69mp7c01">
            <ShieldAlert className="h-4 w-4" data-id="element-976" data-mp="y428jsbhtqkh" /> ব্যবস্থা নিন
          </Button>
          <Button variant="outline" onClick={() => {
          nav.back();
          nav.go('scanner');
        }} className="h-10" data-id="element-977" data-mp="y655yxez6lz8">
            <RotateCcw className="h-4 w-4" data-id="element-978" data-mp="6v7c5hpj1vkz" /> পুনরায় স্ক্যান
          </Button>
        </div>
      </div>

      <AnimatePresence data-id="element-979" data-mp="2tm2s6c7bzfj">
        {sheet && <motion.div className="absolute inset-0 z-20 flex items-end bg-black/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSheet(false)} data-id="element-980" data-mp="2su951260brw">
            <motion.div initial={{
          y: 40
        }} animate={{
          y: 0
        }} exit={{
          y: 40
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="w-full rounded-t-2xl bg-white p-4 pb-6" role="dialog" aria-label="প্রয়োজনীয় ব্যবস্থা নিন" data-id="element-981" data-mp="bvbxbl1drehj">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-200" data-id="element-982" data-mp="3p8opcs10yomu" />
              <h3 className="font-heading text-base font-bold text-navy-900" data-id="element-983" data-mp="31jm83laayxsv">প্রয়োজনীয় ব্যবস্থা নিন</h3>
              <p className="mb-3 text-xs text-slate-500" data-id="element-984" data-mp="2wksu7rr3t4s6">গৃহীত ব্যবস্থা অনিয়ম রিপোর্টে স্বয়ংক্রিয়ভাবে যুক্ত হবে</p>
              <div className="space-y-2" data-id="element-985" data-mp="1c28koagnj5k9">
                {actions.map(({
              label,
              icon: Icon
            }) => <button key={label} onClick={() => {
              setSheet(false);
              nav.showToast(`নির্বাচিত: ${label}`);
              report();
            }} className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-3 text-left text-sm font-medium text-navy-900 hover:bg-slate-50" data-id="element-986" data-mp="1gtgz5kwu9h4a">
                    <Icon className="h-5 w-5 text-navy-700" data-id="element-987" data-mp="1zr9xe9wip2vc" /> {label}
                  </button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}